import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * A Vicidial server. A client may run several, and almost every Vicidial-facing
 * screen needs to say which one a row belongs to — a username, a campaign id and
 * a lead id are only unique *within* one dialer.
 */
export interface Dialer {
  id: string;
  name: string;
  code: string | null;
  base_url: string;
  enabled: boolean;
  mock_mode: boolean;
  last_sync_at: string | null;
  last_error: string | null;
  /** Result of the last Test sync. 'UNKNOWN' until one has been run. */
  connection_state: 'UNKNOWN' | 'OK' | 'FAILED' | 'MOCK';
  connection_checked_at: string | null;
  connection_error: string | null;
  agents_ok: number | null;
  agents_total: number | null;
  /** Whether this server's trunk is actually on the switchboard. */
  /** Empty means this server's own generated endpoint. */
  transfer_trunk?: string | null;
  asterisk_state: string;
  /** Which release generated the config currently on the switchboard. */
  asterisk_applied_version?: string | null;
  asterisk_applied_at: string | null;
  asterisk_error: string | null;
  /** Answers calls on this trunk that matched nothing more specific. */
  default_bot_id: string | null;
}

/**
 * Every Vicidial server configured for the current client.
 *
 * Shared across the Vicidial screens under one query key so switching between
 * them does not refetch, and so a rename shows up everywhere at once.
 */
export function useDialers() {
  return useQuery({
    queryKey: ['vicidial-integrations-select'],
    queryFn: () =>
      api.get<{ data: Dialer[] }>('/vicidial-integrations', { page_size: 100 }),
    staleTime: 60_000,
  });
}

/** Look up a dialer by id — for rendering the owning server in a table cell. */
export function dialerIndex(dialers: Dialer[] | undefined) {
  return new Map((dialers ?? []).map((dialer) => [dialer.id, dialer]));
}

/** How a dialer is written wherever it appears next to something else. */
export function dialerLabel(dialer: Dialer | undefined) {
  if (!dialer) return 'Unassigned';
  return dialer.code ? `${dialer.name} (${dialer.code})` : dialer.name;
}

/**
 * Filter/select options for the dialer.
 *
 * Returns an empty list when the client has one server or none: a filter with a
 * single choice is noise, and this keeps every single-dialer client's screens
 * looking exactly as they did before.
 */
export function dialerOptions(dialers: Dialer[] | undefined) {
  const list = dialers ?? [];
  if (list.length < 2) return [];
  return list.map((dialer) => ({ value: dialer.id, label: dialerLabel(dialer) }));
}
